import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { TextField, Chip } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import Icon from '../../../../atoms/Icon'
import { useListDags } from '../../../../../api/dags'
import { useListDelegatedIdentities } from '../../../../../api/delegatedIdentities'

const NOTIFY_LEVELS = [
  { value: 'trace', label: 'Trace' },
  { value: 'debug', label: 'Debug' },
  { value: 'info', label: 'Info' },
  { value: 'warn', label: 'Warn' },
  { value: 'error', label: 'Error' },
  { value: 'fatal', label: 'Fatal' }
]

const useStyles = makeStyles({
  container: {
    maxWidth: 720,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: '8px 0'
  },
  section: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    overflow: 'hidden'
  },
  sectionHeader: {
    fontSize: 13,
    fontWeight: 600,
    color: '#424242',
    padding: '12px 16px',
    backgroundColor: '#FAFAFA',
    borderBottom: '1px solid #E0E0E0',
    margin: 0
  },
  sectionBody: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  fieldRow: {
    display: 'flex',
    gap: 16
  },
  fieldRowItem: {
    flex: 1
  },
  field: {
    marginBottom: 0
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#757575',
    textTransform: 'uppercase',
    marginBottom: 2
  },
  fieldValue: {
    fontSize: 13,
    wordBreak: 'break-all'
  },
  chainList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4
  },
  emptyChain: {
    fontSize: 12,
    color: '#9E9E9E',
    fontStyle: 'italic'
  }
})

function ReadOnlyField ({ label, value }) {
  const classes = useStyles()
  return (
    <div className={classes.field}>
      <div className={classes.fieldLabel}>{label}</div>
      <div className={classes.fieldValue}>{value ?? '\u2014'}</div>
    </div>
  )
}

ReadOnlyField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any
}

function ChainEditor ({ label, helperText, chains, dagOptions, onChange }) {
  const classes = useStyles()

  const selectedDagIds = useMemo(
    () => (chains || []).map(c => c.$chain).filter(Boolean),
    [chains]
  )

  const available = useMemo(
    () => (dagOptions || []).filter(d => !selectedDagIds.includes(d.dagId)),
    [dagOptions, selectedDagIds]
  )

  const handleAdd = useCallback((_, value) => {
    if (!value) return
    onChange([...(chains || []), { $chain: value.dagId }])
  }, [chains, onChange])

  const handleRemove = useCallback((dagId) => {
    onChange((chains || []).filter(c => c.$chain !== dagId))
  }, [chains, onChange])

  const dagNameMap = useMemo(() => {
    const map = {}
    ;(dagOptions || []).forEach(d => { map[d.dagId] = d.name })
    return map
  }, [dagOptions])

  return (
    <div>
      <div className={classes.fieldLabel}>{label}</div>
      <Autocomplete
        options={available}
        getOptionLabel={(opt) => `${opt.name} (${opt.dagId})`}
        onChange={handleAdd}
        value={null}
        renderInput={(params) => (
          <TextField
            {...params}
            variant='outlined'
            size='small'
            placeholder='Add a workflow...'
            helperText={helperText}
          />
        )}
        size='small'
      />
      {selectedDagIds.length > 0 ? (
        <div className={classes.chainList}>
          {selectedDagIds.map(id => (
            <Chip
              key={id}
              label={dagNameMap[id] || id}
              size='small'
              onDelete={() => handleRemove(id)}
              deleteIcon={<Icon name='close' customSize='14px' />}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

ChainEditor.propTypes = {
  label: PropTypes.string,
  helperText: PropTypes.string,
  chains: PropTypes.array,
  dagOptions: PropTypes.array,
  onChange: PropTypes.func
}

function ReadOnlyChains ({ label, chains, dagOptions }) {
  const classes = useStyles()

  const dagNameMap = useMemo(() => {
    const map = {}
    ;(dagOptions || []).forEach(d => { map[d.dagId] = d.name })
    return map
  }, [dagOptions])

  const items = (chains || []).map(c => c.$chain).filter(Boolean)

  return (
    <div className={classes.field}>
      <div className={classes.fieldLabel}>{label}</div>
      {items.length > 0 ? (
        <div className={classes.chainList}>
          {items.map(id => (
            <Chip key={id} label={dagNameMap[id] || id} size='small' variant='outlined' />
          ))}
        </div>
      ) : (
        <span className={classes.emptyChain}>None</span>
      )}
    </div>
  )
}

ReadOnlyChains.propTypes = {
  label: PropTypes.string,
  chains: PropTypes.array,
  dagOptions: PropTypes.array
}

function SettingsTab ({ dag, editing, editorState, onUpdateDagMeta }) {
  const classes = useStyles()
  const { data: allDags } = useListDags()
  const { data: delegatedIdentities } = useListDelegatedIdentities()
  const isNew = !dag?.dagId || dag?.dagId === 'new'

  const currentIdentityCode = editing
    ? (editorState?.dag?.delegatedIdentityCode ?? '')
    : (dag?.delegatedIdentityCode ?? '')

  const identityOptions = useMemo(() => {
    if (!delegatedIdentities) return []
    return delegatedIdentities
      .filter(di => di.enabled)
      .map(di => ({ value: di.code, label: `${di.displayName} (${di.code})` }))
  }, [delegatedIdentities])

  const currentIdentityLabel = useMemo(() => {
    if (!currentIdentityCode) return null
    return dag?.delegatedIdentityDisplayName || currentIdentityCode
  }, [currentIdentityCode, dag?.delegatedIdentityDisplayName])

  const config = useMemo(
    () => editing ? (editorState?.dag?.configuration ?? {}) : (dag?.configuration ?? {}),
    [editing, editorState?.dag?.configuration, dag?.configuration]
  )
  const workflow = useMemo(() => config.workflow ?? {}, [config])

  const updateConfig = useCallback((updates) => {
    onUpdateDagMeta({ configuration: { ...config, ...updates } })
  }, [config, onUpdateDagMeta])

  const updateWorkflow = useCallback((updates) => {
    onUpdateDagMeta({
      configuration: {
        ...config,
        workflow: { ...workflow, ...updates }
      }
    })
  }, [config, workflow, onUpdateDagMeta])

  const otherDags = useMemo(
    () => (allDags || []).filter(d => d.dagId !== dag?.dagId),
    [allDags, dag]
  )

  return (
    <div className={classes.container}>
      <div className={classes.section}>
        <h4 className={classes.sectionHeader}>Notifications</h4>
        <div className={classes.sectionBody}>
          {editing ? (
            <>
              <TextField
                label='Slack Channel ID'
                variant='outlined'
                size='small'
                value={config.channel || ''}
                onChange={(e) => updateConfig({ channel: e.target.value || null })}
                helperText='The Slack channel to send workflow notifications to'
                fullWidth
              />
              <TextField
                label='Notify Level'
                variant='outlined'
                size='small'
                select
                SelectProps={{ native: true }}
                value={config.notifyLevel || 'error'}
                onChange={(e) => updateConfig({ notifyLevel: e.target.value })}
                helperText='Minimum severity level for Slack notifications'
                fullWidth
              >
                {NOTIFY_LEVELS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </TextField>
            </>
          ) : (
            <div className={classes.fieldRow}>
              <div className={classes.fieldRowItem}>
                <ReadOnlyField label='Slack Channel' value={config.channel} />
              </div>
              <div className={classes.fieldRowItem}>
                <ReadOnlyField label='Notify Level' value={config.notifyLevel || 'error'} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={classes.section}>
        <h4 className={classes.sectionHeader}>Workflow Chaining</h4>
        <div className={classes.sectionBody}>
          {editing ? (
            <>
              <ChainEditor
                label='On Pass'
                helperText='Workflows to trigger when this DAG completes successfully'
                chains={workflow.onPass}
                dagOptions={otherDags}
                onChange={(val) => updateWorkflow({ onPass: val })}
              />
              <ChainEditor
                label='On Fail'
                helperText='Workflows to trigger when this DAG fails'
                chains={workflow.onFail}
                dagOptions={otherDags}
                onChange={(val) => updateWorkflow({ onFail: val })}
              />
              <ChainEditor
                label='On Terminate'
                helperText='Workflows to trigger when this DAG terminates (regardless of outcome)'
                chains={workflow.onTerminate}
                dagOptions={otherDags}
                onChange={(val) => updateWorkflow({ onTerminate: val })}
              />
            </>
          ) : (
            <>
              <ReadOnlyChains label='On Pass' chains={workflow.onPass} dagOptions={allDags} />
              <ReadOnlyChains label='On Fail' chains={workflow.onFail} dagOptions={allDags} />
              <ReadOnlyChains label='On Terminate' chains={workflow.onTerminate} dagOptions={allDags} />
            </>
          )}
        </div>
      </div>

      <div className={classes.section}>
        <h4 className={classes.sectionHeader}>Delegated Identity</h4>
        <div className={classes.sectionBody}>
          {editing ? (
            <TextField
              label='Run As Identity'
              variant='outlined'
              size='small'
              select
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              value={currentIdentityCode}
              onChange={(e) => onUpdateDagMeta({ delegatedIdentityCode: e.target.value || null })}
              helperText='The user identity this workflow assumes when running unattended. Most operations require a user context to resolve data permissions.'
              fullWidth
            >
              <option value=''>— Select an identity —</option>
              {identityOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </TextField>
          ) : (
            <ReadOnlyField label='Run As Identity' value={currentIdentityLabel || 'Not set'} />
          )}
        </div>
      </div>

      {!isNew && dag ? (
        <div className={classes.section}>
          <h4 className={classes.sectionHeader}>Metadata</h4>
          <div className={classes.sectionBody}>
            <div className={classes.fieldRow}>
              <div className={classes.fieldRowItem}>
                <ReadOnlyField label='Created By' value={dag.createdBy} />
              </div>
              <div className={classes.fieldRowItem}>
                <ReadOnlyField
                  label='Created Date'
                  value={dag.createdDate ? dayjs(dag.createdDate).format('MMM D, YYYY h:mm A') : null}
                />
              </div>
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.fieldRowItem}>
                <ReadOnlyField label='Modified By' value={dag.modifiedBy} />
              </div>
              <div className={classes.fieldRowItem}>
                <ReadOnlyField
                  label='Modified Date'
                  value={dag.modifiedDate ? dayjs(dag.modifiedDate).format('MMM D, YYYY h:mm A') : null}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

SettingsTab.propTypes = {
  dag: PropTypes.object,
  editing: PropTypes.bool,
  editorState: PropTypes.object,
  onUpdateDagMeta: PropTypes.func.isRequired
}

export default SettingsTab
